/**
 * Game Launch Service
 * 
 * Handles launching different types of games including:
 * - Regular games with time-period challenges
 * - Continuous games with challenge id 0
 * - External games (handled separately)
 */

import type { Game } from '@/types/game';

export interface GameLaunchOptions {
  gameId: string;
  game: Game;
  challengeId?: number;
}

export interface ContinuousGameLaunchResult {
  gameUrl: string;
  challengeId: number;
  isContinuous: boolean;
}

/**
 * Determines if a game is continuous frequency
 */
export function isContinuousGame(game: Game): boolean {
  return game.challengeFrequency === 'continuous';
}

/**
 * Creates game URL for continuous games using challenge id 0
 */
export function createContinuousGameUrl(game: Game): ContinuousGameLaunchResult {
  const challengeId = 0; // Standard continuous game challenge id
  
  // Get base URL from game configuration
  const baseUrl = game.urlPattern?.subdomain 
    ? `https://${game.urlPattern.subdomain}.imaginesports.com`
    : game.configuration.externalUrl as string;

  // Build URL based on game type
  let gameUrl: string;
  
  if (game.id === 'game_lineup_mlb') {
    // Route-based navigation for MLB Lineup Game
    gameUrl = `${baseUrl}/play/${challengeId}`;
  } else {
    // Query parameter approach for other games
    gameUrl = `${baseUrl}?gameId=${encodeURIComponent(game.id)}&challengeId=${challengeId}`;
  }

  return {
    gameUrl,
    challengeId,
    isContinuous: true
  };
}

/**
 * Handles continuous game launch logic
 */
export function launchContinuousGame(game: Game): ContinuousGameLaunchResult {
  if (!isContinuousGame(game)) {
    throw new Error('Game is not configured for continuous play');
  }

  console.log('[GameLaunchService] Launching continuous game:', {
    gameId: game.id,
    displayName: game.displayName,
    challengeId: 0
  });

  return createContinuousGameUrl(game);
}

/**
 * Determines appropriate launch strategy for a game
 */
export function getGameLaunchStrategy(game: Game): 'continuous' | 'time-period' | 'external' {
  // Check for external games first
  if (game.configuration.gameType === 'external') {
    return 'external';
  }
  
  // Check for continuous frequency
  if (isContinuousGame(game)) {
    return 'continuous';
  }
  
  // Default to time-period challenges
  return 'time-period';
}

/**
 * Validates continuous game configuration
 */
export function validateContinuousGameConfig(game: Game): boolean {
  if (!isContinuousGame(game)) {
    return true; // Not a continuous game, validation passes
  }

  // Check required URL configuration
  const hasUrlPattern = game.urlPattern?.subdomain || game.configuration.externalUrl;
  
  if (!hasUrlPattern) {
    console.error('[GameLaunchService] Continuous game missing URL configuration:', {
      gameId: game.id,
      urlPattern: game.urlPattern,
      externalUrl: game.configuration.externalUrl
    });
    return false;
  }

  return true;
}

/**
 * Error types for game launch failures
 */
export class GameLaunchError extends Error {
  constructor(
    message: string,
    public gameId: string,
    public errorType: 'CONFIGURATION' | 'NETWORK' | 'AUTHENTICATION' | 'UNKNOWN'
  ) {
    super(message);
    this.name = 'GameLaunchError';
  }
}